import { Box, IconButton, InputAdornment, Link, Stack } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  SortingStatus,
  DepositCategory,
  FinancialAccountType,
} from "../../assets/data/Constants";
import dayjs from "dayjs";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import TkButton from "../../components/atoms/TkButton";
import TkDatePicker from "../../components/atoms/TkDatePicker";
import TkLabelText from "../../components/atoms/TkLabelText";
import { TkSelectForm } from "../../components/atoms/TkSelectForm";
import TkTable from "../../components/atoms/TkTable";
import TkCsvUploadSJIS from "../../components/molecules/TkCsvUploadSJIS";
import TkDataTable from "../../components/molecules/TkDataTable";
import { BusinessContext } from "../../context/businessContext";
import { GlobalContext } from "../../context/globalContext";
import { DepositInfoList } from "../../type/apiType/IAPI_REC006";
import {
  IAPI_REC007_Request,
  NonDeliveryDetailsList,
} from "../../type/apiType/IAPI_REC007";
import { PaymentClaimAmountCountList } from "../../type/apiType/IAPI_REC026";
import {
  NonDeliveryDetailResponse,
  TransferResponse,
} from "../../type/componentsType/TkCsvUpload";
import { TkGetCodeList } from "../../utils/TkGetCode";
import "./_style.scss";
import {
  httpAPI_CON001,
  httpAPI_REC001,
  httpAPI_REC006,
  httpAPI_REC007,
  httpAPI_REC026,
} from "./api/request";
import { headTitleList, totalHeadTitleList } from "./parts";
import TkCorporateSearchModal from "../../components/molecules/TkCorporateSearchModal";
import { IAPI_REC001_Response } from "../../type/apiType";
import { changeAmountFormat, toNumberOrUndefined } from "../../utils/useTools";
import { ClearIcon } from "@mui/x-date-pickers";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";

/**
 * SCRW160001 入金管理
 * @return SCRW160001 入金管理
 */
const SCRW160001 = () => {
  const navigate = useNavigate();

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const context = businessState.paymentManagement?.searchForDeposit;
  const [paymentClaimAmountCountList, setPaymentClaimAmountCountList] =
    useState<PaymentClaimAmountCountList[]>([]);
  const [billingKbn, setBillingKbn] = useState<string>("1");
  const [targetMonth, setTargetMonth] = useState<string>("");
  const [totalBodyListData, setTotalBodyListData] = useState<any>();
  const [bodyList, setBodyList] = useState<any>([]);
  const searchValues = useRef<any>({});
  const [corpList, setCorpList] = useState<any>([]);
  const [corpInfo, setCorpInfo] = useState<any>({});
  const [open, setOpen] = useState(false);
  const [openUploadComplete, setOpenUploadComplete] = useState(false);

  let contractorNoData: any;
  switch (state.authInfo?.userType) {
    case authorityUserType.userTypeNumber.KIKO:
      contractorNoData = businessState.common?.contractorNo;
      break;

    case authorityUserType.userTypeNumber.KEIYAKUSYA:
      contractorNoData = state.authInfo?.contractorNo;
      break;
  }

  const handleInit = async () => {
    try {
      const res = await httpAPI_REC026();
      if (res.resultCd === "I0000") {
        const now = new Date();
        let dateFrmat = `${now.getFullYear()}/${now.getMonth() + 1}/${
          now.getDate() - 1
        }`;
        let resObj = {
          ...searchValues.current,
          searchReceiveStatus: SortingStatus["仕分け待ち"],
          searchPaymentDateFrom: dayjs(dateFrmat).format("YYYY/MM/DD"),
          searchPaymentDateTo: dayjs(dateFrmat).format("YYYY/MM/DD"),
        };
        searchValues.current = resObj;

        if (res.paymentClaimAmountCountList) {
          setBusinessState({
            ...businessState,
            paymentManagement: {
              searchForDeposit: {
                ...resObj,
                paymentClaimAmountCountList: res.paymentClaimAmountCountList,
              },
            },
          });
          setPaymentClaimAmountCountList(res.paymentClaimAmountCountList);
        }

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  // 初期表示
  useEffect(() => {
    if (!context) {
      handleInit();
    } else {
      setPaymentClaimAmountCountList(context.paymentClaimAmountCountList ?? []);
      let resObj = { ...context };
      delete resObj.paymentClaimAmountCountList;
      delete resObj.searchContractorName;
      if (context?.searchContractorNo) {
        setCorpInfo({
          contractorNo: context?.searchContractorNo,
          corpName: context?.searchContractorName,
        });
      }
      searchValues.current = resObj;
      handleSearch({ ...resObj });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveBillingKbn = (value: string) => {
    setBillingKbn(value);
    setTargetMonth("");
  };

  const saveTargetMonth = (value: string) => {
    setTargetMonth(value);
  };

  // 区分（合計）
  useEffect(() => {
    if (!paymentClaimAmountCountList) return;

    const filteredBillingKbns: string[] = [];
    Object.values(DepositCategory).forEach((value) => {
      if (
        paymentClaimAmountCountList.some((item) => item.billingKbn === value)
      ) {
        filteredBillingKbns.push(value);
      }
    });
    const filteredBillingKbnEnum = Object.fromEntries(
      Object.entries(DepositCategory).filter(([_, value]) =>
        filteredBillingKbns.includes(value)
      )
    );

    // 累計
    const monthsSet: Set<string> = new Set();
    paymentClaimAmountCountList.forEach((item) => {
      if (item.billingKbn === billingKbn) {
        monthsSet.add(item.aggregationMonth?.toString() || "");
      }
    });

    const months = [
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "10",
      "11",
      "12",
      "1",
      "2",
      "3",
    ];

    const monthObjects = months.map((month) => {
      return { value: month, label: month + "月" };
    });

    const filteredObjects = monthObjects.filter((obj) =>
      monthsSet.has(obj.value)
    );

    const filteredPaymentClaimAmountCount = paymentClaimAmountCountList.filter(
      (item) => {
        if (targetMonth === "") {
          return item.billingKbn === billingKbn;
        } else {
          return (
            item.billingKbn === billingKbn &&
            item.aggregationMonth?.toString() === targetMonth
          );
        }
      }
    );

    const result: any = {};

    filteredPaymentClaimAmountCount.forEach((obj: any) => {
      for (let key in obj) {
        if (result[key] === undefined) {
          result[key] = obj[key];
        } else {
          result[key] += obj[key];
        }
      }
    });

    setTotalBodyListData([
      [
        {
          children: (
            <Stack direction={"row"} spacing={2}>
              <TkSelectForm
                onGetValue={saveBillingKbn}
                placeholder="区分"
                selectPropsList={TkGetCodeList(filteredBillingKbnEnum)}
                wantToSetValue={billingKbn}
              />
              <TkSelectForm
                onGetValue={saveTargetMonth}
                placeholder="累計"
                selectPropsList={filteredObjects}
                wantToSetValue={targetMonth}
              />
            </Stack>
          ),
        },
        {
          children: (result.billingCount ?? 0) + "件",
        },
        {
          children:
            (toNumberOrUndefined(result.billingAmount) ?? 0).toLocaleString() +
            "円",
        },
        {
          align: "center",
          children: (result.depositCount ?? 0) + "件",
        },
        {
          align: "center",
          children:
            (
              toNumberOrUndefined(result.depositAmountTotal) ?? 0
            ).toLocaleString() + "円",
        },
      ],
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentClaimAmountCountList, billingKbn, targetMonth]);

  //仕分け処理をクリック用イベント
  const handleClick = (item: any) => {
    setBusinessState({
      paymentManagement: {
        ...businessState.paymentManagement,
        depositId: item.depositId,
        depositNo: item.depositNo,
        depositDate: item.depositDate,
        fromRequesterName: item.fromRequesterName,
        fromBankName: item.fromBankName,
        fromBranchName: item.fromBranchName,
        sortingStatus: item.sortingStatus,
        depositKbn: item.depositKbn,
        fromBankCd: item.fromBankCd,
        frombranchCd: item.frombranchCd,
        invoiceId: item.invoiceId,
        billingDate: item.billingDate,
        billingContractorNo: item.billingContractorNo,
        billingContractorName: item.billingContractorName,
        billingKbn: item.billingKbn,
        billingAmt: item.billingAmt,
        depositAmt: item.depositAmt,
        billingStaffName: item.billingStaffName,
        billingPrefectureCd: item.billingPrefectureCd,
        nonDeliveryReason: item.nonDeliveryReason,
        searchForDeposit: {
          ...context,
          ...searchValues.current,
          searchContractorName: corpInfo?.corpName,
        },
      },
    });
    navigate("/SCRW160002");
  };

  const isError = (date: any) => {
    return (
      date === "Invalid Date" ||
      dayjs(date).isBefore(dayjs(dateValid.minDate)) ||
      dayjs(date).isAfter(dayjs(dateValid.maxDate))
    );
  };

  const handleSearch = async (param: any = searchValues.current) => {
    if (
      isError(param.searchPaymentDateFrom) ||
      isError(param.searchPaymentDateTo)
    )
      return;

    // 入金・請求一覧参照API 呼び出し
    try {
      const res = await httpAPI_REC001({ ...param });

      if (res.resultCd === "I0000") {
        setBodyList(createBodyList(res));

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const renderDepositType = (depositInfo: any) => {
    if (depositInfo.sortingStatus === SortingStatus["仕分け待ち"]) {
      return (
        <TkButton
          data-testid="fixButton"
          onClick={() => handleClick(depositInfo)}
        >
          仕分け
        </TkButton>
      );
    } else {
      return (
        <Link
          data-testid="invoiceIdLink"
          onClick={() => handleClick(depositInfo)}
        >
          {depositInfo.depositKbn ===
          DepositCategory["組戻金（退職手当金一時預かり金）"]
            ? "退職金一時預かり金"
            : depositInfo.invoiceId ?? depositInfo.depositKbnName}
        </Link>
      );
    }
  };

  const createBodyList = (res: IAPI_REC001_Response, upload?: boolean) => {
    const bodyList = res.depositClaimList?.map((item) => {
      let check = "NG";
      if (
        item.depositAmt &&
        res.unitPremium &&
        (toNumberOrUndefined(item.depositAmt) ?? 0) % res.unitPremium === 0
      ) {
        check = "";
      }
      return [
        {
          align: "center",
          children: item.depositId,
          forSort: item.depositId,
        },
        {
          children: item.depositNo,
          forSort: item.depositNo,
        },
        {
          align: "center",
          children: item.depositDate,
          forSort: item.depositDate,
        },
        {
          align: "right",
          children: changeAmountFormat(item.depositAmt ?? 0),
          forSort: item.depositAmt,
        },
        {
          children: check,
          align: "center",
          forSort: check,
        },
        {
          children: item.fromRequesterName,
          forSort: item.fromRequesterName,
        },
        {
          children: item.fromBankName
            ? (item.fromBankName ?? "") + "/" + (item.fromBranchName ?? "")
            : item.fromBranchName,
          forSort: (item.fromBankName ?? "") + (item.fromBranchName ?? ""),
        },
        {
          children: renderDepositType(item),
        },
      ];
    });

    return bodyList;
  };

  const handleNonDeliveryDetailsUpload = async (
    csvData: NonDeliveryDetailResponse
  ) => {
    let nonDeliveryDetailsList: NonDeliveryDetailsList[] = [];
    csvData.data.forEach((data: any) => {
      nonDeliveryDetailsList.push({
        bankCd: data.bankNumber,
        bankName: data.bankName,
        branchCd: data.branchNumber,
        branchName: data.branchName,
        depositKind: data.depositType,
        billingAccountNo: data.accountNumber.padStart(7, "0"),
        billingAccountHolderKana: data.recipientName,
        amt: toNumberOrUndefined(data.transferAmount),
        nonDeliveryReason: data.dishonorReason,
        cancelKbn: data.cancelType,
      });
    });

    const requestData: IAPI_REC007_Request = {
      initiativeDate:
        (csvData.header.startUpDate.slice(0, 2) ?? "") +
        "." +
        (csvData.header.startUpDate.slice(2, 4) ?? ""),
      toAccountNo: csvData.header.accountNumber,
      nonDeliveryDetailsList: nonDeliveryDetailsList,
      countTotal: csvData.trailer.totalNumber,
      amtTotal: toNumberOrUndefined(csvData.trailer.totalAmount),
      screenId: "SCRW160001",
    };

    // 不着明細取込参照API 呼び出し
    try {
      const res = await httpAPI_REC007(requestData);
      if (res.resultCd === "I0000") {
        setBodyList(createBodyList(res));
        setOpenUploadComplete(true);
        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const handleDepositDataUpload = async (csvData: TransferResponse) => {
    let depositInfoList: DepositInfoList[] = [];
    csvData.data.forEach((data) => {
      depositInfoList.push({
        depositDate: data.startUpDate,
        bankName: data.destinationBankName,
        branchName: data.destinationBranchName,
        amt: toNumberOrUndefined(data.amount),
        status: data.cancelType,
        fromRequesterName: data.transferRequesterName,
        cancelKbn: data.cancelType,
        refNo: data.inquiryNumber.slice(1, 7),
      });
    });

    // 入金帳票取込参照API 呼び出し
    try {
      const res = await httpAPI_REC006({
        depositKind: csvData.header.depositType,
        toAccountNo: csvData.header.accountNumber,
        depositInfoList: depositInfoList,
        countTotal: toNumberOrUndefined(csvData.trailer.totalNumber),
        amtTotal: toNumberOrUndefined(csvData.trailer.totalAmount),
        searchContractorNo: contractorNoData, // 共済契約者番号
        searchAccountNo: searchValues.current.accountNo, // 機構口座番号
        searchReceiveStatus: searchValues.current.searchReceiveStatus, // 紐づけ状況
        searchPaymentDateFrom: searchValues.current.searchPaymentDateFrom, // 入金日From
        searchPaymentDateTo: searchValues.current.searchPaymentDateTo, // 入金日TO
        fileName: csvData.fileName,
      });

      if (res.resultCd === "I0000") {
        setOpenUploadComplete(true);
        setBodyList(createBodyList(res));

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const handleChange = (field: any) => (event: any) => {
    const resObj = {
      ...searchValues.current,
      [field]: event.target.value,
    };
    searchValues.current = resObj;
  };

  const handleCorpSearch = async (data: any) => {
    // 契約者一覧参照 呼び出し
    try {
      const res = await httpAPI_CON001({ ...data });

      if (res.resultCd === "I0000") {
        setState({ ...state, errorMsgId: "" });

        setCorpList(res.contractorList);
      } else {
        setState({ ...state, errorMsgId: res.resultCd });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const handleCorpSave = (data: any) => {
    if (!data) return;

    setOpen(false);
    setCorpInfo({
      contractorNo: data.contractorNo,
      corpName: data.corpName,
    });
    searchValues.current = {
      ...searchValues.current,
      searchContractorNo: data?.contractorNo,
    };
  };

  const dateValid = {
    minDate: new Date("1900/01/01"),
    maxDate: new Date("2099/12/31"),
  };

  const handleClear = () => {
    setCorpInfo({});
    searchValues.current = {
      ...searchValues.current,
      searchContractorNo: null,
    };
  };

  return (
    <Box data-testid="SCRW160001PageForTest" className="tkSCRW160001">
      <Box>
        <TkTable headList={totalHeadTitleList} bodyList={totalBodyListData} />
      </Box>
      <Box className="tkItem">
        <TkSelectForm
          placeholder="区分"
          selectPropsList={TkGetCodeList(DepositCategory)}
          onChange={handleChange("searchDepositKbn")}
          wantToSetValue={context?.searchDepositKbn}
        />

        <Box className="corpSearch">
          <TkLabelText
            variant="standard"
            label="法人名"
            wantToSetValue={
              Object.values(corpInfo)?.length
                ? (corpInfo.contractorNo ?? "") +
                  ":" +
                  (corpInfo.corpName ?? "")
                : ""
            }
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClear}
                    edge="end"
                    sx={{ padding: 0.5, marginRight: 0.2 }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TkButton onClick={() => setOpen(true)}>法人検索</TkButton>
        </Box>

        <TkSelectForm
          placeholder="機構口座番号"
          selectPropsList={TkGetCodeList(FinancialAccountType)}
          onChange={handleChange("searchAccountNo")}
          wantToSetValue={context?.searchAccountNo}
        />

        <TkSelectForm
          placeholder="紐づけ状況"
          selectPropsList={TkGetCodeList(SortingStatus)}
          wantToSetValue={
            context ? context?.searchReceiveStatus : SortingStatus["仕分け待ち"]
          }
          onChange={handleChange("searchReceiveStatus")}
        />
      </Box>

      <Box className="tkItems">
        <Box>入金日：</Box>
        <TkDatePicker
          onChange={handleChange("searchPaymentDateFrom")}
          wantToSetValue={context?.searchPaymentDateFrom}
          {...dateValid}
        />
        <Box>　～　</Box>
        <TkDatePicker
          onChange={handleChange("searchPaymentDateTo")}
          wantToSetValue={context?.searchPaymentDateTo}
          {...dateValid}
        />

        <TkButton onClick={handleSearch}>検索</TkButton>
      </Box>

      {/* 入金請求情報一覧表示 */}
      <TkDataTable
        headList={headTitleList}
        bodyList={bodyList}
        count={bodyList.length}
        defaultRowsPerPage={10}
        pagin
        showUnit
      />

      <Box className="buttonGroup">
        <TkCsvUploadSJIS
          buttonName="①不着明細アップロード"
          onSuccess={handleNonDeliveryDetailsUpload}
          type="nonDeliveryDetail"
        />
        <TkCsvUploadSJIS
          buttonName="②入金データアップロード"
          onSuccess={handleDepositDataUpload}
          type="transferResult"
          id="02"
        />
      </Box>

      <TkCorporateSearchModal
        open={open}
        onClose={() => setOpen(false)}
        onChangeSearchData={handleCorpSearch}
        onSave={handleCorpSave}
        listData={corpList}
      />
      <TkMessageDialog
        open={openUploadComplete}
        onOky={() => setOpenUploadComplete(false)}
        title="アップロード完了"
        contextText="　アップロードが完了しました。"
        okText="はい"
        cancelText=""
      />
    </Box>
  );
};

export default SCRW160001;
